import { AutoComplete } from '@openx/components-core/lib/AutoComplete/AutoComplete';
import {
  AppInventoryOption,
  type AppInventoryOptionType,
  AppInventoryVersion,
  type AppInventoryVersionType,
} from '@openx/types/targeting/appInventory';
import type { InventoryContentOption } from '@openx/types/targeting/inventoryAndContent';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTargetingOptionDisabledState } from '../../../../utils/hooks/useTargetingOptionDisabledState';
import { useContentOptions } from '../../../AppInventory/hooks/useContentOptions';
import { useInventoryContentOptions } from '../../../InventoryAndContent/InventoryAndContentTopBox/hooks/useInventoryContentOptions';

type InventoryContentOptionDropdownProps = {
  onChange:
    | ((type: InventoryContentOption) => void)
    | ((type: AppInventoryOptionType) => void)
    | ((type: InventoryContentOption | AppInventoryOptionType) => void);
  value: InventoryContentOption | AppInventoryOptionType;
  isBulk?: boolean;
  parentAccountFeatures: Record<string, boolean>;
  inventoryVersion?: AppInventoryVersionType;
};

export const InventoryContentOptionDropdown = (props: InventoryContentOptionDropdownProps) => {
  const { onChange, value, isBulk = false, parentAccountFeatures, inventoryVersion } = props;
  const { t } = useTranslation();
  const { getOptionLabel, options } = useInventoryContentOptions({
    isBulk,
    parentAccountFeatures,
  });
  const { getOptionLabel: getOnlyAppOptionLabel, options: onlyAppOptions } = useContentOptions({ isBulk });
  const { isOptionDisabled } = useTargetingOptionDisabledState();

  const autocompleteOptions = useMemo(() => {
    if (inventoryVersion === AppInventoryVersion.APP_INVENTORY_EXCLUDED_V1) {
      return options.filter(
        option => option !== AppInventoryOption.APP_BUNDLE_ID && option !== AppInventoryOption.APP_GROUP
      );
    }

    if (inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2) {
      return onlyAppOptions;
    }

    return options;
  }, [inventoryVersion, onlyAppOptions, options]);

  return (
    <AutoComplete
      textFieldProps={{ label: t('Type') }}
      options={autocompleteOptions}
      renderOptions={{
        dataTest: 'type-option',
      }}
      getOptionLabel={
        inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2 ? getOnlyAppOptionLabel : getOptionLabel
      }
      getOptionDisabled={option => isOptionDisabled(option)}
      textFieldReadOnly
      disableClearable
      filterOptions={option => option}
      onChange={(_e, value) => onChange(value as InventoryContentOption & AppInventoryOptionType)}
      value={value}
      data-test="inventory-content-type"
    />
  );
};
